/**
 * Detects when a previously valid delivery slot becomes unavailable while the user
 * is on the overview or final review screen (i.e. not actively editing the time field),
 * and prompts them to change the start time or keep it for dispatch approval.
 */

import { useOrderChangeSlotValidationMutation } from '@hconnect/tuc-processes/orderManagement'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { validateSlot } from '../../../shared/order-editor/components/fields/Time/api/validateSlot'
import { SlotValidityModal } from '../../../shared/order-editor/components/slotValidity/SlotValidityModal'

export interface OrderManagementSlotValidityCheckerProps {
    // Unique key identifying where the user currently is in the flow (re-triggers validation on change)
    currentState: string
    shouldValidateRoute: boolean
    onChangeStartTime: () => void
}

export const OrderManagementSlotValidityChecker: React.FC<OrderManagementSlotValidityCheckerProps> = ({
    currentState,
    shouldValidateRoute,
    onChangeStartTime
}) => {
    const [isSlotUnavailable, setIsSlotUnavailable] = useState(false)
    const previousAvailability = useRef<boolean | undefined>(undefined)
    const lastValidationKey = useRef<string | undefined>(undefined)

    const { shouldValidate, mutation } = useOrderChangeSlotValidationMutation({
        slotValidationMutationFn: validateSlot
    })
    const { mutateAsync, isPending } = mutation

    useEffect(() => {
        if (!shouldValidateRoute || !shouldValidate || isPending) {
            if (!shouldValidateRoute || !shouldValidate) {
                lastValidationKey.current = undefined
            }
            return
        }

        if (lastValidationKey.current === currentState) return

        lastValidationKey.current = currentState

        mutateAsync()
            .then((response) => {
                const becameUnavailable = previousAvailability.current === true && !response.valid

                previousAvailability.current = response.valid
                setIsSlotUnavailable((currentlyUnavailable) => currentlyUnavailable || becameUnavailable)
            })
            .catch(() => {
                // Request failures are surfaced separately (snackbar) - never block here
            })
    }, [shouldValidateRoute, shouldValidate, isPending, mutateAsync, currentState])

    useEffect(() => {
        if (!shouldValidateRoute || !shouldValidate) {
            setIsSlotUnavailable(false)
        }
    }, [shouldValidateRoute, shouldValidate])

    const handleChangeStartTime = useCallback(() => {
        setIsSlotUnavailable(false)
        onChangeStartTime()
    }, [onChangeStartTime])

    const handleKeepStartTime = useCallback(() => {
        setIsSlotUnavailable(false)
    }, [])

    if (!shouldValidateRoute) {
        return null
    }

    return (
        <SlotValidityModal
            isVisible={isSlotUnavailable}
            onChangeStartTime={handleChangeStartTime}
            onKeepStartTime={handleKeepStartTime}
        />
    )
}
